import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import { useRecoilValue } from 'recoil';
import {fetchAPIResponseSelector} from "../../store/user.atom";
import { useNavigate } from 'react-router-dom';

type Image = {
  id: number;
  imageUrl: string;
};

type Delivery = {
  distance_from: number;
  distance_to: number;
  price: number;
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);

  // State for carousel images
  const [carouselImages, setCarouselImages] = useState<Image[]>([]);
  const [newImage, setNewImage] = useState<string>('');

  // State for delivery interval
  const [deliveryData, setDeliveryData] = useState<Delivery[]>([]);
  const [upperLimit, setUpperLimit] = useState<string>('');
  const [lowerLimit, setLowerLimit] = useState<string>('');
  const [price, setPrice] = useState<string>('');

  // Fetch delivery intervals from API
  useEffect(() => {
    const fetchDeliveryIntervals = async () => {
      try {
        const response = await fetchAPIResponse<Delivery[]>('/settings/delivery/intervals', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
        setDeliveryData(response.data);
      } catch (error) {
        console.error('Error fetching delivery intervals:', error);
      }
    };

    fetchDeliveryIntervals();
  }, [fetchAPIResponse]);

  // Handler for adding image
  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setCarouselImages(prevImages => [...prevImages, { id: Date.now(), imageUrl }]);
    }
  };

  // Handler for deleting image
  const handleDeleteImage = (id: number) => {
    const updatedImages = carouselImages.filter(image => image.id !== id);
    setCarouselImages(updatedImages);
  };

  // Handler for adding delivery
  const handleAddDelivery = async () => {
    if (upperLimit.trim() !== '' && lowerLimit.trim() !== '' && price.trim() !== '') {
      const newDelivery: Delivery = { distance_from: Number(lowerLimit),
        distance_to: Number(upperLimit), price: Number(price) };
      const updatedDeliveryData = [...deliveryData, newDelivery];

      try {
        await fetchAPIResponse('/settings/delivery/intervals', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(updatedDeliveryData),
        });

        setDeliveryData(updatedDeliveryData);
        setUpperLimit('');
        setLowerLimit('');
        setPrice('');
      } catch (error) {
        console.error('Error updating delivery intervals:', error);
      }
    }
  };

  // Handler for deleting delivery
  const handleDeleteDelivery = async (index: number) => {
    const updatedDelivery = deliveryData.filter((_, i) => i !== index);

    try {
      await fetchAPIResponse('/settings/delivery/intervals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(updatedDelivery),
      });

      setDeliveryData(updatedDelivery);
    } catch (error) {
      console.error('Error deleting delivery interval:', error);
    }
  };

  return (
      <div className='flex flex-col items-center w-full h-full overflow-auto py-10 gap-6'>
        <Header title={'Settings'} button={false} buttonTitle={''} action={''}/>
        <div className="zbh-card w-11/12 p-8">
          {/* Delivery Interval section */}
          <div>
            <h2 className="font-display text-base font-semibold text-ink">Delivery intervals</h2>
            <p className="mt-1 text-sm text-categoriesTextColor">Set the delivery fee for each distance band, measured from the restaurant.</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="zbh-eyebrow mb-1.5 block">From (km)</label>
                <input type="text" placeholder="0" value={lowerLimit} onChange={(e) => setLowerLimit(e.target.value)} className="zbh-input" />
              </div>
              <div>
                <label className="zbh-eyebrow mb-1.5 block">To (km)</label>
                <input type="text" placeholder="5" value={upperLimit} onChange={(e) => setUpperLimit(e.target.value)} className="zbh-input" />
              </div>
              <div>
                <label className="zbh-eyebrow mb-1.5 block">Fee ($)</label>
                <input type="text" placeholder="3" value={price} onChange={(e) => setPrice(e.target.value)} className="zbh-input" />
              </div>
            </div>
            <button onClick={handleAddDelivery} className="zbh-btn-accent mt-4">Add interval</button>
          </div>

          <div className='w-full mt-8'>
              <div className='grid grid-cols-12 gap-4 px-2'>
                <div className="zbh-th col-span-4">From (km)</div>
                <div className="zbh-th col-span-4">To (km)</div>
                <div className="zbh-th col-span-2">Fee</div>
                <div className="zbh-th col-span-2 text-right">Action</div>
              </div>
              <div className="zbh-tear mt-2" />
              {deliveryData.map((delivery, index) => (
                  <div key={index} className='grid grid-cols-12 items-center gap-4 px-2 py-3 border-b border-hair last:border-0 transition hover:bg-canvas/60 rounded-lg'>
                    <div className='col-span-4 zbh-figure text-sm text-ink'>{delivery.distance_from}</div>
                    <div className='col-span-4 zbh-figure text-sm text-ink'>{delivery.distance_to}</div>
                    <div className='col-span-2 zbh-figure text-sm font-bold text-ink'>${delivery.price}</div>
                    <div className='col-span-2 flex items-center justify-end'>
                      <button onClick={() => handleDeleteDelivery(index)} className="zbh-btn-danger px-4 py-1.5 text-xs">Delete</button>
                    </div>
                  </div>
              ))}
          </div>
        </div>
      </div>
  );
};

export default Settings;
